import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { GlobalSchemas } from "@/components/SEO";
import { GAPageView } from "@/components/GAPageView";
import PromoBanner from "@/components/PromoBanner";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";

import appCss from "../styles.css?url";

// Ported from the pre-migration index.html <head>: canonical host + legacy
// WordPress URL redirect. Runs before React boots.
const canonicalRedirectScript = String.raw`(function () {
  try {
    var h = window.location.hostname;
    var p = window.location.pathname;
    var s = window.location.search;
    var hash = window.location.hash;

    // 1. Force www on the apex production domain.
    if (h === 'garden-review.co.uk') {
      window.location.replace('https://www.garden-review.co.uk' + p + s + hash);
      return;
    }

    // 2. Strip legacy WordPress date paths: /YYYY/MM/DD/slug[/] -> /articles/slug
    var m = p.match(/^\/(\d{4})\/(\d{2})\/(\d{2})\/([^\/]+)\/?$/);
    if (m) {
      window.location.replace('https://www.garden-review.co.uk/articles/' + m[4] + s + hash);
      return;
    }

    // 3. Specific article renames
    var articleRedirects = {
      '/articles/summer-gardening-tips-for-a-hotter-drier-uk': '/articles/7-ways-to-Help-Your-Garden-Survive-a-UK-Heatwave'
    };

    // 4. Product category moves
    var productRedirects = {
      '/lawn-mowers/hyundai-HYWT5200X-52cc-petrol-wheeled-grass-trimmer-43cm-cutting-width-large-wheel': '/strimmers/hyundai-HYWT5200X-52cc-petrol-wheeled-grass-trimmer-43cm-cutting-width-large-wheel',
      '/lawn-mowers/hyundai-HYBC5600X-56cc-petrol-brushcutter-and-grass-trimmer-2-in-1-includes-harnes': '/strimmers/hyundai-HYBC5600X-56cc-petrol-brushcutter-and-grass-trimmer-2-in-1-includes-harnes',
      '/lawn-mowers/hyundai-HYTR60LI-60v-cordless-battery-grass-trimmer-bare-unit-33cm-cutting-width': '/strimmers/hyundai-HYTR60LI-60v-cordless-battery-grass-trimmer-bare-unit-33cm-cutting-width'
    };
    var productRedirectTarget = productRedirects[p];
    if (productRedirectTarget) {
      window.location.replace('https://www.garden-review.co.uk' + productRedirectTarget + s + hash);
      return;
    }

    var redirectTarget = articleRedirects[p];
    if (redirectTarget) {
      window.location.replace('https://www.garden-review.co.uk' + redirectTarget + s + hash);
      return;
    }

    // 5. Inject a default canonical for first-pass crawlers. The
    // MutationObserver removes this default once Helmet writes its per-page
    // canonical, so we never ship two <link rel="canonical"> tags.
    if (!document.querySelector('link[rel="canonical"]')) {
      var canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      canon.setAttribute('href', 'https://www.garden-review.co.uk' + p);
      canon.setAttribute('data-default-canonical', '');
      document.head.appendChild(canon);
      if (typeof MutationObserver !== 'undefined') {
        var obs = new MutationObserver(function () {
          var links = document.querySelectorAll('link[rel="canonical"]');
          if (links.length > 1) {
            var def = document.querySelector('link[rel="canonical"][data-default-canonical]');
            if (def) def.parentNode.removeChild(def);
            obs.disconnect();
          }
        });
        obs.observe(document.head, { childList: true, subtree: false });
      }
    }
  } catch (e) {}
})();`;

// Ported from the pre-migration index.html: GA4 bootstrap with deferred
// loading of gtag.js and the Awin affiliate tag behind interaction/idle.
const analyticsScript = String.raw`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
if (!window.location.pathname.startsWith('/admin')) {
  gtag('config', 'G-QT5WZ1M0HL', { send_page_view: false });
}
(function () {
  var restLoaded = false;
  function loadRest() {
    if (restLoaded) return; restLoaded = true;
    var gs = document.createElement('script');
    gs.src = 'https://www.googletagmanager.com/gtag/js?id=G-QT5WZ1M0HL';
    gs.async = true; document.head.appendChild(gs);
    var aw = document.createElement('script');
    aw.src = 'https://www.dwin2.com/pub.1218722.min.js';
    aw.async = true; aw.defer = true; document.body.appendChild(aw);
    // AdSense (Auto Ads) waits for the same first interaction / idle point.
    var ad = document.createElement('script');
    ad.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6025261484028438';
    ad.async = true; ad.crossOrigin = 'anonymous'; document.head.appendChild(ad);
    // Microsoft Clarity (session heatmaps/recordings), loaded with the rest.
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', 'z2pqlfhc25');
    ['pointerdown','keydown','scroll','touchstart'].forEach(function(e){
      window.removeEventListener(e, loadRest, { passive: true });
    });
  }
  ['pointerdown','keydown','scroll','touchstart'].forEach(function(e){
    window.addEventListener(e, loadRest, { passive: true, once: true });
  });
  if ('requestIdleCallback' in window) {
    requestIdleCallback(function(){ setTimeout(loadRest, 3000); }, { timeout: 6000 });
  } else {
    setTimeout(loadRest, 4000);
  }
})();`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      // NOTE: no default <title> here. Page components render their own
      // <title> (hoisted by React 19), and a root-level title would ship a
      // second <title> tag in the server HTML on every page.
      // NOTE: page-overridable tags (description, robots, og:*, twitter:*)
      // are rendered via Helmet in RootComponent so that page-level Helmet
      // tags REPLACE them instead of emitting a second tag.
      // Site verification tags (Impact uses a non-standard `value` attribute)
      {
        name: "impact-site-verification",
        value: "93decaff-a53a-4657-a48c-48a019d8819c",
      } as unknown as { name: string; content: string },
      { name: "google-site-verification", content: "JJCeb9RCAfJfkuAtZG0UZQId8HJS4Tgrp5vaDvdUzAY" },
      { name: "p:domain_verify", content: "ad85ac96a744f93e06b356b699bf0a94" },
      { name: "author", content: "Garden-Review" },
      { name: "geo.region", content: "GB" },
      { name: "geo.placename", content: "United Kingdom" },
      { name: "language", content: "English" },
      { property: "og:site_name", content: "Garden-Review" },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:site", content: "@GardenReviewUK" },
      { name: "theme-color", content: "#16a34a" },
      { name: "msapplication-TileColor", content: "#16a34a" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap",
      },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://hgaughoritzdekpahoih.supabase.co" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      // Feed discovery — feeds are intentionally not listed in sitemap.xml
      // (a sitemap lists indexable HTML pages, not syndication endpoints).
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Garden-Review RSS Feed",
        href: "https://www.garden-review.co.uk/rss.xml",
      },
    ],

    scripts: [
      { children: canonicalRedirectScript },
      { children: analyticsScript },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => <NotFound />,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-GB" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hasRouteSharingUrl = useRouterState({
    select: (s) => s.matches.some((match) =>
      match.meta?.some((meta) => !!meta && "property" in meta && meta.property === "og:url"),
    ),
  });
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        {/* Only the self-referencing og:url is emitted site-wide. Page-level
            SEO tags (description, robots, og:*, twitter:*) are rendered by each
            page — duplicating them here would ship two of every tag, since
            React 19 hoists both the root and the page tags into <head>. */}
        {!hasRouteSharingUrl && (
          <Helmet>
            <meta property="og:url" content={`https://www.garden-review.co.uk${pathname}`} />
          </Helmet>
        )}


        <TooltipProvider>
          <GlobalSchemas />
          <Toaster />
          <Sonner />
          <AuthProvider>
            {/* Temporary site-wide top promo bar (Prime Day deals) */}
            <PromoBanner />
            <GAPageView />
            <Outlet />
          </AuthProvider>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center">
        <h1 className="text-xl font-bold text-foreground mb-2">This page didn&apos;t load</h1>
        <p className="text-muted-foreground mb-6">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex gap-2 justify-center flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="px-4 py-2 rounded-md border border-border bg-card text-foreground hover:bg-muted transition-colors"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
